import type { LogicalBounds } from '../../../shared/layout/index.js';
import { NUMBER_KINGDOM_ASSETS } from '../NumberKingdomAssets.js';

const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';

export type NumberKingdomHomeButtonSide = 'left' | 'right';

/** Production-art navigation control shared by every Number Kingdom destination. */
export class NumberKingdomHomeButton {
  readonly root: Phaser.GameObjects.Container;
  private readonly frame: Phaser.GameObjects.Image;
  private readonly label: Phaser.GameObjects.Text;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly onActivate: () => void,
    depth = 1000,
  ) {
    // Scenes already lay this control out in camera-visible logical bounds.
    // A zero scroll factor shifts it out of view when ENVELOP camera zoom > 1.
    this.root = scene.add.container(0, 0).setDepth(depth).setScrollFactor(1);
    this.frame = scene.add.image(0, 0, NUMBER_KINGDOM_ASSETS.homeHubButtonFrame.key)
      .setDisplaySize(180, 60)
      .setInteractive({ useHandCursor: true });
    this.label = scene.add.text(0, 0, '←  HOME HUB', {
      fontFamily: FONT_UI,
      fontSize: '15px',
      fontStyle: 'bold',
      color: '#fff8d8',
      stroke: '#173b76',
      strokeThickness: 4,
      shadow: { offsetX: 0, offsetY: 2, blur: 2, color: '#091b42', fill: true },
    }).setOrigin(0.5);

    this.frame.on('pointerdown', this.press, this);
    this.frame.on('pointerout', this.release, this);
    this.frame.on('pointerup', this.activate, this);
    this.root.add([this.frame, this.label]);
  }

  layout(safe: LogicalBounds, compact: boolean, side: NumberKingdomHomeButtonSide = 'left') {
    const width = compact ? 156 : 180;
    const height = compact ? 52 : 60;
    const edge = compact ? 78 : 90;
    const inset = 24;
    this.root.setPosition(
      side === 'left' ? safe.x + edge + inset : safe.right - edge - inset,
      safe.y + height * 0.5 + inset,
    );
    this.frame.setDisplaySize(width, height);
    this.label.setFontSize(compact ? 13 : 15);
    return this;
  }

  private readonly press = () => {
    this.root.setScale(0.97).setAlpha(0.9);
  };

  private readonly release = () => {
    this.root.setScale(1).setAlpha(1);
  };

  private readonly activate = () => {
    this.release();
    this.onActivate();
  };

  destroy() {
    this.frame.off('pointerdown', this.press, this);
    this.frame.off('pointerout', this.release, this);
    this.frame.off('pointerup', this.activate, this);
    this.root.destroy(true);
  }
}

export function createNumberKingdomHomeButton(
  scene: Phaser.Scene,
  onActivate: () => void,
  depth?: number,
) {
  return new NumberKingdomHomeButton(scene, onActivate, depth);
}
